<script setup>
import * as qs from 'qs'
import IconEuro from '~/components/icons/IconEuro'
import IconGem from '~/components/icons/IconGem'
import IconGift from '~/components/icons/IconGift'
import IconStar from '~/components/icons/IconStar'
import { parseFaqAnswer } from '~/utils/faqMarkdown'

definePageMeta({
  layout: 'partner',
})

const { data: partnerData } = await useSubdomainData()

const iconsMappings = {
  'bi-cash-coin': IconEuro,
  'bi-gift-fill': IconGift,
  'bi-ticket-fill': IconGem,
  'bi-star-fill': IconStar,
}
const { locale } = useI18n()

const pageData = await useStrapiPage({
  slug: `giftcard-booking-solution_${locale.value}`,
  locale: convertLocaleIntoBCP47(locale.value),
})
const bannerBlock = computed(() =>
  pageData?.data?.value?.pages?.[0]?.contents?.find(
    (b) => b.__typename === 'ComponentSharedBanner'
  )
)

const keys = ['mainSummaryList', 'secondarySummaryList', 'faq', 'seo']
const { data: strapiData } = await useStrapiSingleType(
  `/booking-solution-giftcard-page?${qs.stringify(
    {
      locale: locale.value,
      populate: keys.reduce(
        (carry, key) => ({
          ...carry,
          [key]: { populate: '*' },
        }),
        {}
      ),
    },
    { encodeValuesOnly: false, arrayFormat: 'comma' }
  )}`,
  {
    transform: ({ data = {} }) => data,
  }
)

const getFaqQuestions = computed(() => {
  let items = (strapiData.value.faq?.questions || []).map((q) => ({
    question: q.question,
    // Strapi answers are markdown; partner faq_blocks below stay as-is
    answer: parseFaqAnswer(q.answer),
  }))

  if (partnerData.value?.booking_solution_settings?.data?.faq_blocks?.length) {
    items = [
      ...items,
      ...(partnerData.value?.booking_solution_settings?.data?.faq_blocks ?? []),
    ]
  }

  return items
})

const bannerStrapiData = computed(() => {
  const mainMediaBlock = bannerBlock.value?.mainMediaBlock
  return {
    bannerTitle: mainMediaBlock?.title,
    bannerDescription: mainMediaBlock?.subtitle,
    bannerBgImageSrc: mainMediaBlock?.media?.url,
  }
})

const computedData = computed(() => {
  return {
    mainSummaryListItems: (
      (strapiData.value || {}).mainSummaryList?.items || []
    ).map((d) => [
      {
        ...d,
        ...(iconsMappings[d.icon_class_name]
          ? { iconComp: iconsMappings[d.icon_class_name] }
          : null),
      },
    ]),
    secondarySummaryList:
      strapiData.value?.secondarySummaryList?.items?.map((item) => ({
        label: item.label,
        icon: iconsMappings[item.icon_class_name],
      })) || [],
    faq: {
      sections: [
        {
          title: strapiData.value.faq.title || undefined,
          questions: getFaqQuestions.value,
        },
      ],
    },
    seo: (strapiData.value || {}).seo,
    grids:
      partnerData.value?.booking_solution_settings?.data?.gift_card_blocks ||
      [],
    banner:
      partnerData.value?.booking_solution_settings?.data?.gift_card_page_image
        ?.data?.url || undefined,
  }
})
</script>

<template>
  <div class="max-w-(--breakpoint-xl) w-full m-auto px-4">
    <GiftcardContent
      :display-banner-giftcard-box="false"
      :display-giftcard-events-showcase="false"
      :display-giftcard-details-section="false"
      :display-event-showcase="false"
      :giftcard-icons-list="computedData.secondarySummaryList"
      :icon-heading-list="computedData.mainSummaryListItems"
      :faq="computedData.faq"
      :grids="computedData.grids"
      :banner="computedData.banner"
      v-bind="bannerStrapiData"
    >
    </GiftcardContent>
  </div>
</template>
